<template>
    <div>
        <BackHeader
            title="Representantes"
            :onBack="() => $router.push('/configuracion')"
        ></BackHeader>

        <p class="mb-6">
            Son las personas que aparecen como contacto y a cargo del sistema de
            prácticas: en las preguntas frecuentes, en la información del proceso
            y en los correos que reciben los alumnos. Cada campus tiene su jefe o
            jefa de carrera, que va siempre primero y no se puede eliminar;
            mientras no se complete, no aparece como contacto. Arrastra un
            encargado por su manija para cambiar el orden en que aparecen.
        </p>

        <v-row>
            <!-- Lista de representantes -->
            <v-col cols="12" md="4">
                <v-card class="pa-3">
                    <v-btn
                        block
                        elevation="0"
                        color="primary"
                        class="mb-3"
                        :disabled="creando"
                        @click="siNoHayCambios(crear)"
                    >
                        <v-icon left small>mdi-plus</v-icon>
                        Nuevo representante
                    </v-btn>

                    <!-- Jefes de carrera: uno por campus, fijos arriba. Un campus
                         cuyo jefe no existe en la base muestra igual su espacio,
                         para crearlo desde aquí. -->
                    <div
                        v-for="representante in jefes"
                        :key="clave(representante)"
                        class="fila"
                        :class="{ activa: esActivo(representante) }"
                        @click="seleccionar(representante)"
                    >
                        <v-icon small class="mr-2">mdi-lock-outline</v-icon>
                        <div class="texto">
                            <div v-if="representante.nombre" class="font-weight-medium">
                                {{ representante.nombre }}
                            </div>
                            <div v-else class="font-italic">Sin asignar</div>
                            <div class="caption">{{ representante.cargo_completo }}</div>
                        </div>
                    </div>

                    <draggable
                        v-model="encargados"
                        handle=".manija"
                        @start="ordenPrevio = encargados.slice()"
                        @end="guardarOrden"
                    >
                        <div
                            v-for="representante in encargados"
                            :key="representante.id"
                            class="fila"
                            :class="{ activa: esActivo(representante) }"
                            @click="seleccionar(representante)"
                        >
                            <v-icon small class="manija mr-2">mdi-drag</v-icon>
                            <div class="texto">
                                <div class="font-weight-medium">{{ representante.nombre }}</div>
                                <div class="caption">{{ representante.cargo_completo }}</div>
                            </div>
                        </div>
                    </draggable>

                    <p
                        v-if="!jefes.length && !encargados.length && !cargando"
                        class="caption text-center my-3"
                    >
                        Todavía no hay representantes.
                    </p>
                </v-card>
            </v-col>

            <!-- Edición del elegido -->
            <v-col cols="12" md="8" class="panel-edicion">
                <v-card v-if="formulario" class="pa-4">
                    <v-text-field
                        v-model="formulario.nombre"
                        label="Nombre"
                        filled
                        :error-messages="errores.nombre"
                    ></v-text-field>

                    <v-select
                        v-if="formulario.es_jefe_carrera"
                        v-model="formulario.cargo"
                        :items="cargosJefeCarrera"
                        label="Cargo"
                        filled
                        :error-messages="errores.cargo"
                    ></v-select>
                    <v-text-field
                        v-else
                        v-model="formulario.cargo"
                        label="Cargo"
                        filled
                        :error-messages="errores.cargo"
                    ></v-text-field>

                    <!-- El campus de un jefe de carrera es fijo: se muestra pero
                         no se cambia. -->
                    <v-select
                        v-model="formulario.campus"
                        :items="campus"
                        item-text="nombre"
                        item-value="id"
                        label="Campus"
                        filled
                        :disabled="formulario.es_jefe_carrera"
                        :error-messages="errores.campus"
                    ></v-select>

                    <v-text-field
                        v-model="formulario.correo"
                        label="Correo"
                        type="email"
                        filled
                        :error-messages="errores.correo"
                    ></v-text-field>

                    <v-switch
                        v-model="formulario.correo_recordatorio"
                        label="Enviar correos recordatorios"
                        hint="Cada lunes recibe un correo con la cantidad de prácticas en revisión."
                        persistent-hint
                        inset
                        class="mt-0 mb-4"
                        :error-messages="errores.correo_recordatorio"
                    ></v-switch>

                    <v-card-actions class="px-0">
                        <v-btn
                            elevation="0"
                            color="primary"
                            :disabled="!hayCambios || guardando"
                            @click="guardar"
                        >
                            {{ formulario.id ? "Guardar cambios" : "Crear representante" }}
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                            v-if="formulario.id && !formulario.es_jefe_carrera"
                            text
                            color="error"
                            @click="pedirBorrado"
                        >
                            Eliminar representante
                        </v-btn>
                        <v-btn v-else-if="!formulario.id" text @click="cerrar">
                            Cancelar
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-else class="pa-8 text-center">
                    <p class="mb-0">
                        Elige un representante de la lista para editarlo.
                    </p>
                </v-card>
            </v-col>
        </v-row>

        <ActionModal
            :active="confirmarBorrado"
            title="Eliminar representante"
            :successAction="borrar"
            :cancelAction="() => (confirmarBorrado = false)"
        >
            {{ textoBorrado }}
        </ActionModal>

        <ActionModal
            :active="confirmarDescarte"
            title="Cambios sin guardar"
            :successAction="descartarYSeguir"
            :cancelAction="() => ((confirmarDescarte = false), (pendiente = null))"
        >
            Tienes cambios sin guardar. Si continúas se pierden.
        </ActionModal>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import BackHeader from "@/components/content/BackHeader";
import ActionModal from "@/components/ActionModal";
import { tablesService } from "@/services";

const CAMPOS = ["nombre", "cargo", "correo", "campus", "correo_recordatorio"];

// Los únicos cargos de un jefe de carrera; el backend no acepta otros.
const CARGOS_JEFE_CARRERA = ["Jefe de carrera", "Jefa de carrera"];

function sinErrores() {
    return { nombre: [], cargo: [], correo: [], campus: [], correo_recordatorio: [] };
}

export default {
    components: { draggable, BackHeader, ActionModal },

    data: () => ({
        // /api/representantes/ trae primero los jefes de carrera, uno por
        // campus y fijos, y después los encargados, que son los únicos que se
        // reordenan y se borran.
        jefes: [],
        encargados: [],
        campus: [],
        cargosJefeCarrera: CARGOS_JEFE_CARRERA,
        // { id, nombre, cargo, correo, campus, es_jefe_carrera,
        // correo_recordatorio }; sin id
        // mientras es un encargado nuevo, o el jefe de un campus que no lo
        // tiene en la base, y todavía no se guarda.
        formulario: null,
        original: null,
        ordenPrevio: [],
        cargando: true,
        guardando: false,
        confirmarBorrado: false,
        confirmarDescarte: false,
        // Lo que se hace si el administrador acepta descartar sus cambios.
        pendiente: null,
        // Se fija al abrir el aviso para que no cambie mientras se desvanece.
        textoBorrado: "",
        errores: sinErrores(),
    }),

    computed: {
        // Un encargado nuevo, todavía sin guardar.
        creando() {
            return (
                !!this.formulario && !this.formulario.id && !this.formulario.es_jefe_carrera
            );
        },

        hayCambios() {
            if (!this.formulario || !this.original) {
                return false;
            }
            return CAMPOS.some(
                (campo) => this.formulario[campo] !== this.original[campo]
            );
        },
    },

    created() {
        this.cargar();
    },

    methods: {
        cargar() {
            this.cargando = true;
            Promise.all([
                tablesService.getService("representantes"),
                tablesService.getService("campus"),
            ])
                .then(([representantes, campus]) => {
                    this.campus = campus.slice().sort((a, b) => a.id - b.id);
                    // Un espacio por campus, en el orden de los campus: el jefe
                    // que trae la base o, si no existe, uno vacío sin id.
                    this.jefes = this.campus.map(
                        (c) =>
                            representantes.find(
                                (r) => r.es_jefe_carrera && r.campus === c.id
                            ) || {
                                id: null,
                                nombre: "",
                                cargo: CARGOS_JEFE_CARRERA[0],
                                cargo_completo: `${CARGOS_JEFE_CARRERA[0]} ${c.nombre}`,
                                correo: "",
                                campus: c.id,
                                es_jefe_carrera: true,
                                // Un jefe de carrera nace sin recordatorios.
                                correo_recordatorio: false,
                            }
                    );
                    this.encargados = representantes.filter((r) => !r.es_jefe_carrera);
                    this.cargando = false;
                })
                .catch(() => {
                    this.cargando = false;
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudieron cargar los representantes."
                    );
                });
        },

        // Identifica una fila aunque no tenga id: el espacio de jefe de un
        // campus que no lo tiene en la base se reconoce por su campus.
        clave(representante) {
            if (representante.id) {
                return `id-${representante.id}`;
            }
            return representante.es_jefe_carrera ? `campus-${representante.campus}` : "nuevo";
        },

        esActivo(representante) {
            return !!this.formulario && this.clave(this.formulario) === this.clave(representante);
        },

        seleccionar(representante) {
            if (this.esActivo(representante)) {
                return;
            }
            this.siNoHayCambios(() => this.abrir(representante));
        },

        // Todo lo que reemplaza el formulario pasa por aquí: con cambios sin
        // guardar, primero se pide confirmación.
        siNoHayCambios(accion) {
            if (this.hayCambios) {
                this.pendiente = accion;
                this.confirmarDescarte = true;
                return;
            }
            accion();
        },

        descartarYSeguir() {
            this.confirmarDescarte = false;
            const accion = this.pendiente;
            this.pendiente = null;
            if (accion) {
                accion();
            }
        },

        abrir(representante) {
            this.errores = sinErrores();
            this.formulario = {
                id: representante.id,
                nombre: representante.nombre,
                cargo: representante.cargo,
                correo: representante.correo,
                campus: representante.campus,
                es_jefe_carrera: representante.es_jefe_carrera,
                correo_recordatorio: representante.correo_recordatorio,
            };
            // Copia aparte para saber si hay cambios sin guardar.
            this.original = { ...this.formulario };
        },

        // Uno nuevo no se crea en el backend hasta guardarlo: nombre, cargo,
        // correo y campus son obligatorios y un alta vacía la rechazaría. Por
        // aquí solo se crean encargados: el jefe de un campus se crea desde su
        // espacio en la lista.
        crear() {
            this.abrir({
                id: null,
                nombre: "",
                cargo: "",
                correo: "",
                campus: null,
                es_jefe_carrera: false,
                correo_recordatorio: true,
            });
        },

        cerrar() {
            this.formulario = null;
            this.original = null;
        },

        guardar() {
            this.guardando = true;
            this.errores = sinErrores();

            const datos = {
                nombre: this.formulario.nombre,
                cargo: this.formulario.cargo,
                correo: this.formulario.correo,
                correo_recordatorio: this.formulario.correo_recordatorio,
            };
            const nuevo = !this.formulario.id;
            const jefe = this.formulario.es_jefe_carrera;
            // El campus de un jefe de carrera que ya existe no se manda: no se
            // puede cambiar.
            if (!jefe || nuevo) {
                datos.campus = this.formulario.campus;
            }
            const peticion = nuevo
                ? tablesService.newService("representantes", {
                      ...datos,
                      es_jefe_carrera: jefe,
                      // Los jefes no se ordenan; un encargado va al final.
                      orden: jefe
                          ? 0
                          : this.encargados.length
                          ? Math.max(...this.encargados.map((r) => r.orden)) + 1
                          : 1,
                  })
                : tablesService.editService(
                      "representantes",
                      `${this.formulario.id}/`,
                      datos
                  );

            peticion
                .then((representante) => {
                    if (representante.es_jefe_carrera) {
                        // Reemplaza su espacio, exista o no en la base hasta ahora.
                        const posicion = this.jefes.findIndex(
                            (item) => item.campus === representante.campus
                        );
                        this.$set(this.jefes, posicion, representante);
                    } else if (nuevo) {
                        this.encargados.push(representante);
                    } else {
                        const posicion = this.encargados.findIndex(
                            (item) => item.id === representante.id
                        );
                        this.$set(this.encargados, posicion, representante);
                    }
                    this.abrir(representante);
                    this.guardando = false;
                    this.$store.dispatch(
                        "alert/success",
                        nuevo ? "Representante creado." : "Representante guardado."
                    );
                })
                .catch((error) => {
                    this.guardando = false;
                    this.mostrarErrores(error);
                });
        },

        pedirBorrado() {
            this.textoBorrado =
                `¿Eliminar a «${this.original.nombre}»? Dejará de aparecer como ` +
                "contacto en las páginas y en los correos, y no se puede deshacer.";
            this.confirmarBorrado = true;
        },

        borrar() {
            const { id } = this.formulario;
            this.confirmarBorrado = false;

            tablesService
                .deleteService("representantes", `${id}/`)
                .then(() => {
                    this.encargados = this.encargados.filter(
                        (representante) => representante.id !== id
                    );
                    this.cerrar();
                    this.$store.dispatch("alert/success", "Representante eliminado.");
                })
                .catch(() => {
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudo eliminar el representante."
                    );
                });
        },

        // Se manda la lista completa de encargados: el backend la exige para
        // que un orden calculado sobre una pantalla desactualizada falle en vez
        // de dejar huecos. Si falla, la lista vuelve a como estaba.
        guardarOrden() {
            const ids = (lista) => lista.map((item) => item.id).join(",");
            if (ids(this.encargados) === ids(this.ordenPrevio)) {
                return;
            }
            const previo = this.ordenPrevio;
            tablesService
                .newService("representantes/reordenar", {
                    orden: this.encargados.map((representante) => representante.id),
                })
                .then((representantes) => {
                    this.encargados = representantes.filter((r) => !r.es_jefe_carrera);
                })
                .catch(() => {
                    this.encargados = previo;
                    this.$store.dispatch("alert/error", "No se pudo guardar el orden.");
                });
        },

        mostrarErrores(error) {
            const datos = error && error.response && error.response.data;
            const errores = sinErrores();
            if (datos) {
                CAMPOS.forEach((campo) => {
                    const valor = datos[campo];
                    errores[campo] = !valor ? [] : Array.isArray(valor) ? valor : [valor];
                });
            }
            this.errores = errores;
            if (!CAMPOS.some((campo) => errores[campo].length)) {
                this.$store.dispatch("alert/error", "No se pudo guardar.");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.fila {
    display: flex;
    align-items: center;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
        background-color: rgba(128, 128, 128, 0.12);
    }

    &.activa {
        border-color: var(--v-primary-base);
    }

    .texto {
        flex: 1;
        min-width: 0;

        div {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

.fila + .fila {
    margin-top: 4px;
}

.manija {
    cursor: grab;
}

// Igual que en las preguntas frecuentes: el panel de edición acompaña el
// scroll para que el elegido siga a la vista aunque la lista sea larga.
@media (min-width: 960px) {
    .panel-edicion {
        position: sticky;
        top: 12px;
        align-self: flex-start;
    }
}
</style>
